import type { DerivedThreadStatus } from "@/types/threadCardStatus.ts";
import type { SimpleTaskCategory, SimpleTaskGroup, UserTask } from "@/types/userTasks.ts";

/**
 * Words for the simpler Tasks list (`tasks-simple-list`). The server groups
 * and sorts it (`api/src/tasks/simpleList.ts`); these are what both apps call
 * what it sends.
 */
export const SIMPLE_CATEGORY_LABEL: Record<SimpleTaskCategory, string> = {
  open: "Open",
  snoozed: "Snoozed",
  closed: "Closed",
};

export const SIMPLE_GROUP_HEADING: Record<SimpleTaskGroup["key"], string> = {
  suggested: "Suggested",
  open: "Open",
  snoozed: "Snoozed",
  closed: "Closed",
};

export const SIMPLE_EMPTY_TEXT: Record<SimpleTaskCategory, string> = {
  open: "Nothing on your plate.",
  snoozed: "Nothing snoozed.",
  closed: "Nothing closed yet.",
};

/**
 * The day a snoozed task comes back, short enough to sit beside its title:
 * "Back Tue, Nov 10". `locale` is for tests; the apps leave it to the device.
 */
export function snoozedBackLabel(
  task: Pick<UserTask, "status" | "snoozedUntilMs">,
  locale?: string,
): string | null {
  if (task.status !== "later" || !task.snoozedUntilMs) return null;
  const day = new Date(task.snoozedUntilMs).toLocaleDateString(locale, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
  return `Back ${day}`;
}

/**
 * Where a task's thread stands, in the words View thread shows beside it.
 * The status is the one the thread's home card computes; the words are the
 * task list's own. A paused thread needs the user to resume it, so it reads
 * as needing them. Null when the task has no thread, or a server too old to
 * say.
 */
export function taskThreadStatusLabel(
  task: Pick<UserTask, "threadStatus" | "waitingOn">,
): string | null {
  switch (task.threadStatus) {
    case "inProgress":
      return "Running";
    case "needsAttention":
    case "paused":
      return "Needs you";
    case "waiting":
      return task.waitingOn?.name ? `Waiting on ${task.waitingOn.name}` : "Waiting on someone";
    case "completed":
      return "Completed";
    case "archived":
      return "Archived";
    default:
      return null;
  }
}

/** The card status whose mark a task's View thread borrows; paused wears needing-you's. */
export function taskThreadMark(
  status: DerivedThreadStatus | null | undefined,
): DerivedThreadStatus | null {
  if (!status) return null;
  return status === "paused" ? "needsAttention" : status;
}
